/**
 * SLAM Athletics Theme System
 * Supports both light and dark modes with the SLAM color scheme
 */
import React, { createContext, useState, useContext, useEffect } from 'react';

export const colors = {
  // Primary brand colors
  yellow: {
    DEFAULT: '#FFD100', // SLAM Yellow
    light: '#FFE04D',
    dark: '#FFBB00',
  },
  black: {
    DEFAULT: '#000000', // SLAM Black
    light: '#222222',
    dark: '#000000',
  },
  white: '#FFFFFF',
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
};

// Light theme (default)
export const lightTheme = {
  name: 'light',
  colors: {
    primary: colors.yellow.DEFAULT,
    secondary: colors.black.DEFAULT,
    background: {
      primary: colors.white,
      secondary: colors.gray[50],
      tertiary: colors.gray[100],
      contrast: colors.black.DEFAULT,
      card: colors.white,
      nav: colors.black.DEFAULT,
    },
    text: {
      primary: colors.black.DEFAULT,
      secondary: colors.gray[700],
      tertiary: colors.gray[500],
      inverse: colors.white,
      accent: colors.yellow.DEFAULT,
    },
    border: {
      light: colors.gray[200],
      medium: colors.gray[300],
      dark: colors.gray[400],
    },
    button: {
      primary: {
        bg: colors.yellow.DEFAULT,
        hover: colors.yellow.dark,
        text: colors.black.DEFAULT,
      },
      secondary: {
        bg: colors.black.DEFAULT,
        hover: colors.black.light,
        text: colors.white,
      },
      outline: {
        border: colors.gray[300],
        hover: colors.gray[200],
        text: colors.black.DEFAULT,
      },
    },
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
  },
};

// Dark theme
export const darkTheme = {
  name: 'dark',
  colors: {
    primary: colors.yellow.DEFAULT,
    secondary: colors.white,
    background: {
      primary: colors.black.DEFAULT,
      secondary: colors.gray[900],
      tertiary: colors.gray[800],
      contrast: colors.white,
      card: colors.gray[800],
      nav: colors.black.DEFAULT,
    },
    text: {
      primary: colors.white,
      secondary: colors.gray[300],
      tertiary: colors.gray[400],
      inverse: colors.black.DEFAULT,
      accent: colors.yellow.DEFAULT,
    },
    border: {
      light: colors.gray[700],
      medium: colors.gray[600],
      dark: colors.gray[500],
    },
    button: {
      primary: {
        bg: colors.yellow.DEFAULT,
        hover: colors.yellow.dark,
        text: colors.black.DEFAULT,
      },
      secondary: {
        bg: colors.gray[800],
        hover: colors.gray[700],
        text: colors.white,
      },
      outline: {
        border: colors.gray[600],
        hover: colors.gray[700],
        text: colors.white,
      },
    },
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
  },
};

// Create Theme Context
export const ThemeContext = createContext({
  theme: lightTheme,
  setTheme: () => {},
  toggleTheme: () => {},
});

// Theme Provider component
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    // Check if user has a preference stored
    if (savedTheme) {
      return savedTheme === 'dark' ? darkTheme : lightTheme;
    }
    // Default to dark theme for all users
    return darkTheme;
  });

  // Apply theme class to document
  useEffect(() => {
    const root = window.document.documentElement;
    // Apply theme class based on current selection
    if (theme.name === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }

    // Save preference
    localStorage.setItem('theme', theme.name);
  }, [theme]);

  // Function to toggle between themes
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme.name === 'light' ? darkTheme : lightTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Export default theme (light)
export default lightTheme;